import { IProductAvailability } from '@core/product/models/product-availability.model';
import { IProductBasicInfo } from '@core/product/models/product-basic-info.model';
import { IProductGiftcard } from '@core/product/models/product-giftcard.model';
import { IAgreement, IProductInfo } from '@core/product/models/product-info.model';
import { IProductMeta } from '@core/product/models/product-meta.model';
import { IProductPackageSession, IProductSession } from '@core/product/models/product-session.model';
import { TProductType } from '@core/product/models/product-type.type';
import { IModifierGroup } from './product-modifiers.models';
import { IRestrictedAddons } from './restricted-addons.model';
import { IVariationRatio } from './product-variation-ratio.model';
import { TDurationType } from './product-duration-type.type';

/** Loosely matches Roller.Core.Domain.DTO.Checkout.CheckoutProductWithAvailabilities - mixed with Roller.Core.API.Product.ProductDetail */
export interface IProduct extends IProductBasicInfo {
  addOns: string[];
  availabilities?: IProductAvailability[];
  products: IProductInfo[];
  sessions?: IProductSession[];
  addonList?: IProduct[]; // internal use only

  parentProductId?: string;
  parentProductName?: string;
  depositPercentage?: number;
  depositAmount?: number;
  formIds?: string[];
  order?: number;
  validDays?: number;
  validDaysFromRedemption?: number;
  isWaiverRequired?: boolean;
  isSuspended?: boolean; // only FE
  meta?: IProductMeta[] | [];
  /** The booking cost modifier IDs linked to this product. */
  modifiers?: string[];
  /** The booking cost modifier groups linked to this product */
  modifierGroups?: IModifierGroup[];
  giftcard?: IProductGiftcard;
  /** Local use only for calculating packages subtype. As keyof typeof TProductType. */
  packageSubType?: TProductType[];
  agreement?: IAgreement;
  cancellationTermsText?: string;
  captureTicketHolderName?: boolean;
  hideSessionDuration?: boolean;
  packageSessions?: IProductPackageSession[]; // local use only for grouping sessions by date and name
  restrictedAddOns: IRestrictedAddons[];
  /** If a product is of type party package, this value is provided*/
  partyDetails?: {
    partyNamePrompt?: string;
    guestListEnabled?: boolean;
  };
  isOnlineSalesClosed?: boolean; // local use only
  isSoldOut?: boolean; // local use only
  /** Sessions are only visible for session based products that have a date selected. */
  isItemSessionBased?: boolean;
  /** Date selection is visible for session based products and some standard passes (and packages that meet the same conditions) */
  isItemDateBased?: boolean;
  /** Minimum product purchase, across variations. Default is 1 */
  minPurchase: number;
  maxPurchase?: number;
  /** Required ticket ratios for session passes */
  variationRatioRequirements?: IVariationRatio[];
  pricingRuleId?: string;
  displayRemainingResourcesInsteadOfTickets?: boolean;
  durationType?: TDurationType;
  duration?: number;
  isSelectAddonNextAvailableSession?: boolean;
  sessionBufferMinutes?: number;
}

export enum EProductTypes {
  Pass = 6,
  AddOn = 7,
  Custom = 8,
  GiftCard = 9,
  Package = 10,
  Wallet = 11,
  CashlessCard = 12,
  PartyPackage = 13
}

export enum EProductSubTypes {
  Donation = 1,
  Tip = 2,
  OpenProduct = 3,
  RecurringSessions = 4,
  Membership = 5,
  ExternalGiftCard = 6,
  Session = 7,
  Stock = 8
}
